/**
 * SpryBoard Collaboration (SpryWorkspace Backend)
 *
 * Replaces the encrypted P2P relay with SpryWorkspace-backend plaintext Socket.IO.
 * Server handles room management via join-board / leave-board events.
 * Data is sent in plaintext (no client-side E2E encryption).
 */
import {
  CaptureUpdateAction,
  getSceneVersion,
  restoreElements,
  zoomToFitBounds,
  reconcileElements,
} from "@excalidraw/excalidraw";
import { ErrorDialog } from "@excalidraw/excalidraw/components/ErrorDialog";
import { APP_NAME, EVENT } from "@excalidraw/common";
import {
  IDLE_THRESHOLD,
  ACTIVE_THRESHOLD,
  UserIdleState,
  assertNever,
  isDevEnv,
  isTestEnv,
  preventUnload,
  resolvablePromise,
  throttleRAF,
} from "@excalidraw/common";
import { getVisibleSceneBounds } from "@excalidraw/element";
import { newElementWith } from "@excalidraw/element";
import { isImageElement, isInitializedImageElement } from "@excalidraw/element";
import { AbortError } from "@excalidraw/excalidraw/errors";
import { t } from "@excalidraw/excalidraw/i18n";
import { withBatchedUpdates } from "@excalidraw/excalidraw/reactUtils";

import throttle from "lodash.throttle";
import { PureComponent } from "react";
import { SpryCollaborationCrdt } from "./crdt";

import type {
  ReconciledExcalidrawElement,
  RemoteExcalidrawElement,
} from "@excalidraw/excalidraw/data/reconcile";
import type { ImportedDataState } from "@excalidraw/excalidraw/data/types";
import type {
  ExcalidrawElement,
  FileId,
  InitializedExcalidrawImageElement,
  OrderedExcalidrawElement,
} from "@excalidraw/element/types";
import type {
  BinaryFileData,
  ExcalidrawImperativeAPI,
  SocketId,
  Collaborator,
  Gesture,
} from "@excalidraw/excalidraw/types";
import type { Mutable, ValueOf } from "@excalidraw/common/utility-types";

import { appJotaiStore, atom } from "../app-jotai";
import {
  CURSOR_SYNC_TIMEOUT,
  FILE_UPLOAD_MAX_BYTES,
  FIREBASE_STORAGE_PREFIXES,
  INITIAL_SCENE_UPDATE_TIMEOUT,
  LOAD_IMAGES_TIMEOUT,
  WS_SUBTYPES,
  SYNC_FULL_SCENE_INTERVAL_MS,
  WS_EVENTS,
} from "../app_constants";
import {
  generateCollaborationLinkData,
  getCollaborationLink,
  getSyncableElements,
} from "../data";
import {
  encodeFilesForUpload,
  FileManager,
  updateStaleImageStatuses,
} from "../data/FileManager";
import { LocalData } from "../data/LocalData";
import {
  isSavedToBackend,
  loadFilesFromBackend,
  loadFromBackend,
  saveFilesToBackend,
  saveToBackend,
} from "../data/RestBackend";
import {
  importUsernameFromLocalStorage,
  saveUsernameToLocalStorage,
} from "../data/localStorage";
import { resetBrowserStateVersions } from "../data/tabSync";

import { collabErrorIndicatorAtom } from "./CollabError";
import Portal from "./Portal";

import { getAccessToken } from "../api/backend";

import type {
  SocketUpdateDataSource,
  SyncableExcalidrawElement,
} from "../data";

type BoardCrdtSnapshot = {
  boardId: string;
  elements: readonly OrderedExcalidrawElement[];
  lastUpdatedAt: number;
};

type BoardCrdtPresenceState = {
  userId: string;
  username: string;
  avatarUrl?: string;
  color?: string;
  pointer?: Collaborator["pointer"];
  userState?: Collaborator["userState"];
  isCurrentUser?: boolean;
  lastActiveAt: string;
};

const resolveSocketUrl = () => {
  const explicit =
    import.meta.env.VITE_APP_WS_SERVER_URL ||
    import.meta.env.VITE_WS_SERVER_URL ||
    import.meta.env.VITE_SOCKET_URL;

  if (explicit) {
    return explicit;
  }

  const apiUrl =
    import.meta.env.VITE_APP_API_URL ||
    import.meta.env.VITE_API_URL;

  if (apiUrl) {
    try {
      const parsed = new URL(apiUrl);
      return `${parsed.protocol}//${parsed.host}`;
    } catch {
      // Fall back to localhost below.
    }
  }

  return "http://localhost:3001";
};

export const collabAPIAtom = atom<CollabAPI | null>(null);
export const isCollaboratingAtom = atom(false);
export const isOfflineAtom = atom(false);

interface CollabState {
  errorMessage: string | null;
  dialogNotifiedErrors: Record<string, boolean>;
  username: string;
  activeRoomLink: string | null;
}

export const activeRoomLinkAtom = atom<string | null>(null);

type CollabInstance = InstanceType<typeof Collab>;

export interface CollabAPI {
  isCollaborating: () => boolean;
  onPointerUpdate: CollabInstance["onPointerUpdate"];
  startCollaboration: CollabInstance["startCollaboration"];
  stopCollaboration: CollabInstance["stopCollaboration"];
  syncElements: CollabInstance["syncElements"];
  getCrdt: CollabInstance["getCrdt"];
  encodeCrdtUpdate: CollabInstance["encodeCrdtUpdate"];
  applyCrdtUpdate: CollabInstance["applyCrdtUpdate"];
  fetchImageFilesFromBackend: CollabInstance["fetchImageFilesFromBackend"];
  setUsername: CollabInstance["setUsername"];
  getUsername: CollabInstance["getUsername"];
  getActiveRoomLink: CollabInstance["getActiveRoomLink"];
  setCollabError: CollabInstance["setErrorDialog"];
}

interface CollabProps {
  excalidrawAPI: ExcalidrawImperativeAPI;
  currentUserId?: string | null;
  currentUserAvatar?: string;
}

class Collab extends PureComponent<CollabProps, CollabState> {
  portal: Portal;
  fileManager: FileManager;
  excalidrawAPI: CollabProps["excalidrawAPI"];
  activeIntervalId: number | null;
  idleTimeoutId: number | null;

  private socketInitializationTimer?: number;
  private lastBroadcastedOrReceivedSceneVersion: number = -1;
  private collaborators = new Map<SocketId, Collaborator>();
  private crdt: SpryCollaborationCrdt<BoardCrdtSnapshot, BoardCrdtPresenceState> | null = null;

  constructor(props: CollabProps) {
    super(props);
    this.state = {
      errorMessage: null,
      dialogNotifiedErrors: {},
      username: importUsernameFromLocalStorage() || "",
      activeRoomLink: null,
    };
    this.portal = new Portal(this);
    this.fileManager = new FileManager({
      getFiles: async (fileIds) => {
        const { roomId, roomKey } = this.portal;
        if (!roomId || !roomKey) {
          throw new AbortError();
        }
        return loadFilesFromBackend(`files/rooms/${roomId}`, roomKey, fileIds);
      },
      saveFiles: async ({ addedFiles }) => {
        const { roomId, roomKey } = this.portal;
        if (!roomId || !roomKey) {
          throw new AbortError();
        }
        const { savedFiles, erroredFiles } = await saveFilesToBackend({
          prefix: `${FIREBASE_STORAGE_PREFIXES.collabFiles}/${roomId}`,
          files: await encodeFilesForUpload({
            files: addedFiles,
            encryptionKey: roomKey,
            maxBytes: FILE_UPLOAD_MAX_BYTES,
          }),
        });

        return {
          savedFiles: savedFiles.reduce(
            (acc: Map<FileId, BinaryFileData>, id) => {
              const fileData = addedFiles.get(id);
              if (fileData) {
                acc.set(id, fileData);
              }
              return acc;
            },
            new Map(),
          ),
          erroredFiles: erroredFiles.reduce(
            (acc: Map<FileId, BinaryFileData>, id) => {
              const fileData = addedFiles.get(id);
              if (fileData) {
                acc.set(id, fileData);
              }
              return acc;
            },
            new Map(),
          ),
        };
      },
    });
    this.excalidrawAPI = props.excalidrawAPI;
    this.activeIntervalId = null;
    this.idleTimeoutId = null;
  }

  private onUmmount: (() => void) | null = null;

  componentDidMount() {
    window.addEventListener(EVENT.BEFORE_UNLOAD, this.beforeUnload);
    window.addEventListener("online", this.onOfflineStatusToggle);
    window.addEventListener("offline", this.onOfflineStatusToggle);
    window.addEventListener(EVENT.UNLOAD, this.onUnload);

    const unsubOnUserFollow = this.excalidrawAPI.onUserFollow((payload) => {
      this.portal.socket && this.portal.broadcastUserFollowed(payload);
    });
    const throttledRelayUserViewportBounds = throttleRAF(
      this.relayVisibleSceneBounds,
    );
    const unsubOnScrollChange = this.excalidrawAPI.onScrollChange(() =>
      throttledRelayUserViewportBounds(),
    );
    this.onUmmount = () => {
      unsubOnUserFollow();
      unsubOnScrollChange();
    };

    this.onOfflineStatusToggle();

    const collabAPI: CollabAPI = {
      isCollaborating: this.isCollaborating,
      onPointerUpdate: this.onPointerUpdate,
      startCollaboration: this.startCollaboration,
      syncElements: this.syncElements,
      getCrdt: this.getCrdt,
      encodeCrdtUpdate: this.encodeCrdtUpdate,
      applyCrdtUpdate: this.applyCrdtUpdate,
      fetchImageFilesFromBackend: this.fetchImageFilesFromBackend,
      stopCollaboration: this.stopCollaboration,
      setUsername: this.setUsername,
      getUsername: this.getUsername,
      getActiveRoomLink: this.getActiveRoomLink,
      setCollabError: this.setErrorDialog,
    };

    appJotaiStore.set(collabAPIAtom, collabAPI);

    if (isTestEnv() || isDevEnv()) {
      window.collab = window.collab || ({} as Window["collab"]);
      Object.defineProperties(window, {
        collab: {
          configurable: true,
          value: this,
        },
      });
    }
  }

  componentDidUpdate(prevProps: CollabProps) {
    if (
      prevProps.currentUserId !== this.props.currentUserId ||
      prevProps.currentUserAvatar !== this.props.currentUserAvatar
    ) {
      const collaborators = new Map<SocketId, Collaborator>();
      this.collaborators.forEach((collaborator, socketId) => {
        const isCurrentUser = this.isCurrentUser(socketId);
        collaborators.set(socketId, {
          ...collaborator,
          isCurrentUser,
          avatarUrl:
            isCurrentUser && this.props.currentUserAvatar
              ? this.props.currentUserAvatar
              : collaborator.avatarUrl,
        });
      });
      this.collaborators = collaborators;
      this.excalidrawAPI.updateScene({ collaborators });
      this.syncCollaboratorsToCrdt("spryboard:presence:props-updated");
    }
  }

  onOfflineStatusToggle = () => {
    appJotaiStore.set(isOfflineAtom, !window.navigator.onLine);
  };

  componentWillUnmount() {
    window.removeEventListener("online", this.onOfflineStatusToggle);
    window.removeEventListener("offline", this.onOfflineStatusToggle);
    window.removeEventListener(EVENT.BEFORE_UNLOAD, this.beforeUnload);
    window.removeEventListener(EVENT.UNLOAD, this.onUnload);
    window.removeEventListener(EVENT.POINTER_MOVE, this.onPointerMove);
    window.removeEventListener(
      EVENT.VISIBILITY_CHANGE,
      this.onVisibilityChange,
    );
    if (this.activeIntervalId) {
      window.clearInterval(this.activeIntervalId);
      this.activeIntervalId = null;
    }
    if (this.idleTimeoutId) {
      window.clearTimeout(this.idleTimeoutId);
      this.idleTimeoutId = null;
    }
    this.onUmmount?.();
  }

  isCollaborating = () => appJotaiStore.get(isCollaboratingAtom)!;

  getCrdt = () => this.crdt;

  encodeCrdtUpdate = (stateVector?: Uint8Array) => {
    return this.crdt?.encodeUpdate(stateVector) ?? null;
  };

  applyCrdtUpdate = (update: Uint8Array, origin = "spryboard:manual:update") => {
    this.crdt?.applyUpdate(update, origin);
  };

  private setIsCollaborating = (isCollaborating: boolean) => {
    appJotaiStore.set(isCollaboratingAtom, isCollaborating);
  };

  private initializeCrdt = (
    boardId: string,
    elements: readonly OrderedExcalidrawElement[] | readonly ExcalidrawElement[],
  ) => {
    this.crdt?.destroy();
    this.crdt = new SpryCollaborationCrdt<BoardCrdtSnapshot, BoardCrdtPresenceState>({
      name: "spryboard",
      initialSnapshot: {
        boardId,
        elements: [...elements] as readonly OrderedExcalidrawElement[],
        lastUpdatedAt: Date.now(),
      },
    });
    this.crdt.setMetadata("boardId", boardId, "spryboard:meta:board");
    this.crdt.setMetadata("username", this.state.username, "spryboard:meta:user");
  };

  private replaceBoardSnapshot = (
    elements: readonly OrderedExcalidrawElement[] | readonly ExcalidrawElement[],
    origin: string,
  ) => {
    const boardId = this.portal.roomId;
    if (!this.crdt || !boardId) {
      return;
    }
    this.crdt.replaceSnapshot(
      {
        boardId,
        elements: [...elements] as readonly OrderedExcalidrawElement[],
        lastUpdatedAt: Date.now(),
      },
      origin,
    );
  };

  private syncCollaboratorsToCrdt = (origin: string) => {
    if (!this.crdt) {
      return;
    }
    const states = Object.fromEntries(
      Array.from(this.collaborators.entries()).map(([socketId, collaborator]) => [
        socketId,
        {
          userId: socketId,
          username: collaborator.username || "",
          avatarUrl: collaborator.avatarUrl,
          color: collaborator.color,
          pointer: collaborator.pointer,
          userState: collaborator.userState,
          isCurrentUser: collaborator.isCurrentUser,
          lastActiveAt: new Date().toISOString(),
        } satisfies BoardCrdtPresenceState,
      ]),
    );
    this.crdt.replacePresenceStates(states, origin);
  };

  private onUnload = () => {
    this.destroySocketClient({ isUnload: true });
  };

  private beforeUnload = withBatchedUpdates((event: BeforeUnloadEvent) => {
    const syncableElements = getSyncableElements(
      this.getSceneElementsIncludingDeleted(),
    );

    if (
      this.isCollaborating() &&
      (this.fileManager.shouldPreventUnload(syncableElements) ||
        !isSavedToBackend(this.portal, syncableElements))
    ) {
      this.saveCollabRoomToBackend(syncableElements);

      if (import.meta.env.VITE_APP_DISABLE_PREVENT_UNLOAD !== "true") {
        preventUnload(event);
      }
    }
  });

  saveCollabRoomToBackend = async (
    syncableElements: readonly SyncableExcalidrawElement[],
  ) => {
    try {
      const storedElements = await saveToBackend(
        this.portal,
        syncableElements,
        this.excalidrawAPI.getAppState(),
      );

      this.resetErrorIndicator();

      if (this.isCollaborating() && storedElements) {
        this.handleRemoteSceneUpdate(this._reconcileElements(storedElements));
      }
    } catch (error: any) {
      const errorMessage = /is longer than.*?bytes/.test(error.message)
        ? t("errors.collabSaveFailed_sizeExceeded")
        : t("errors.collabSaveFailed");

      if (
        !this.state.dialogNotifiedErrors[errorMessage] ||
        !this.isCollaborating()
      ) {
        this.setErrorDialog(errorMessage);
        this.setState({
          dialogNotifiedErrors: {
            ...this.state.dialogNotifiedErrors,
            [errorMessage]: true,
          },
        });
      }

      if (this.isCollaborating()) {
        this.setErrorIndicator(errorMessage);
      }

      console.error(error);
    }
  };

  // Alias for backward compatibility
  saveCollabRoomToFirebase = this.saveCollabRoomToBackend;

  stopCollaboration = (keepRemoteState = true) => {
    this.queueBroadcastAllElements.cancel();
    this.queueSaveToBackend.cancel();
    this.loadImageFiles.cancel();
    this.resetErrorIndicator(true);

    this.saveCollabRoomToBackend(
      getSyncableElements(
        this.excalidrawAPI.getSceneElementsIncludingDeleted(),
      ),
    );

    if (this.portal.socket && this.fallbackInitializationHandler) {
      this.portal.socket.off(
        "connect_error",
        this.fallbackInitializationHandler,
      );
    }

    if (!keepRemoteState) {
      LocalData.fileStorage.reset();
      this.destroySocketClient();
    } else if (window.confirm(t("alerts.collabStopOverridePrompt"))) {
      resetBrowserStateVersions();

      window.history.pushState({}, APP_NAME, window.location.origin);
      this.destroySocketClient();

      LocalData.fileStorage.reset();

      const elements = this.excalidrawAPI
        .getSceneElementsIncludingDeleted()
        .map((element) => {
          if (isImageElement(element) && element.status === "saved") {
            return newElementWith(element, { status: "pending" });
          }
          return element;
        });

      this.excalidrawAPI.updateScene({
        elements,
        captureUpdate: CaptureUpdateAction.NEVER,
      });
    }
  };

  private destroySocketClient = (opts?: { isUnload: boolean }) => {
    this.lastBroadcastedOrReceivedSceneVersion = -1;
    this.portal.close();
    this.fileManager.reset();
    this.crdt?.destroy();
    this.crdt = null;
    if (!opts?.isUnload) {
      this.setIsCollaborating(false);
      this.setActiveRoomLink(null);
      this.collaborators = new Map();
      this.excalidrawAPI.updateScene({
        collaborators: this.collaborators,
      });
      LocalData.resumeSave("collaboration");
    }
  };

  private fetchImageFilesFromBackend = async (opts: {
    elements: readonly ExcalidrawElement[];
    forceFetchFiles?: boolean;
  }) => {
    const unfetchedImages = opts.elements
      .filter((element) => {
        return (
          isInitializedImageElement(element) &&
          !this.fileManager.isFileTracked(element.fileId) &&
          !element.isDeleted &&
          (opts.forceFetchFiles
            ? element.status !== "pending" ||
            Date.now() - element.updated > 10000
            : element.status === "saved")
        );
      })
      .map((element) => (element as InitializedExcalidrawImageElement).fileId);

    return await this.fileManager.getFiles(unfetchedImages);
  };

  private fallbackInitializationHandler: null | (() => any) = null;

  startCollaboration = async (
    existingRoomLinkData: null | { roomId: string; roomKey: string },
    options?: { preloadedScene?: boolean },
  ) => {
    if (!this.state.username) {
      import("@excalidraw/random-username").then(({ getRandomUsername }) => {
        const username = getRandomUsername();
        this.setUsername(username);
      });
    }

    if (this.portal.socket) {
      return null;
    }

    // When the SpryBoard app already loaded the scene from the backend (auto-room-link
    // for boardId/fileId routes), we must NOT call resetScene() or re-fetch from the
    // backend — that wipes the loaded scene and causes a flash-then-clear.
    const preloadedScene = options?.preloadedScene === true;

    let roomId;
    let roomKey;

    if (existingRoomLinkData) {
      ({ roomId, roomKey } = existingRoomLinkData);
    } else {
      ({ roomId, roomKey } = await generateCollaborationLinkData());
      window.history.pushState(
        {},
        APP_NAME,
        getCollaborationLink({ roomId, roomKey }),
      );
    }

    const scenePromise = resolvablePromise<
      | (ImportedDataState & { elements: readonly OrderedExcalidrawElement[] })
      | null
    >();

    this.setIsCollaborating(true);
    LocalData.pauseSave("collaboration");
    this.initializeCrdt(roomId, this.excalidrawAPI.getSceneElementsIncludingDeleted());

    const { default: socketIOClient } = await import(
      /* webpackChunkName: "socketIoClient" */ "socket.io-client"
    );

    const fallbackInitializationHandler = () => {
      this.initializeRoom({
        roomLinkData: existingRoomLinkData,
        fetchScene: !preloadedScene,
        preloadedScene,
      } as any).then((scene) => {
        scenePromise.resolve(scene);
      });
    };
    this.fallbackInitializationHandler = fallbackInitializationHandler;

    try {
      // Connect to SpryWorkspace backend with auth token
      const wsUrl = resolveSocketUrl();
      const token = getAccessToken();

      this.portal.socket = this.portal.open(
        socketIOClient(wsUrl, {
          transports: ["websocket", "polling"],
          auth: {
            token: token || "",
          },
        }),
        roomId,
        roomKey,
      );

      this.portal.socket.once("connect_error", fallbackInitializationHandler);
    } catch (error: any) {
      console.error(error);
      this.setErrorDialog(error.message);
      return null;
    }

    if (existingRoomLinkData && !preloadedScene) {
      // Joining a remote shared collaboration room — discard local state and
      // wait for the remote scene.
      this.excalidrawAPI.resetScene();
    } else if (!existingRoomLinkData) {
      const elements = this.excalidrawAPI.getSceneElements().map((element) => {
        if (isImageElement(element) && element.status === "saved") {
          return newElementWith(element, { status: "pending" });
        }
        return element;
      });
      this.excalidrawAPI.updateScene({
        elements,
        captureUpdate: CaptureUpdateAction.NEVER,
      });

      this.saveCollabRoomToBackend(getSyncableElements(elements));
    }

    // Fallback initialization timeout
    this.socketInitializationTimer = window.setTimeout(
      fallbackInitializationHandler,
      INITIAL_SCENE_UPDATE_TIMEOUT,
    );

    // Listen for board updates from other users (plaintext, no decryption)
    this.portal.socket.on(
      "board-updated",
      (data: {
        userId: string;
        elements: any[];
        appState?: any;
        timestamp: string;
      }) => {
        if (!data.elements) {
          return;
        }

        // First message initializes the room
        if (!this.portal.socketInitialized) {
          this.initializeRoom({ fetchScene: false });
          const reconciledElements = this._reconcileElements(data.elements);
          this.handleRemoteSceneUpdate(reconciledElements);
          scenePromise.resolve({
            elements: reconciledElements,
            scrollToContent: true,
          });
        } else {
          this.handleRemoteSceneUpdate(
            this._reconcileElements(data.elements),
          );
        }
      },
    );

    // Board pointer updates from other users
    this.portal.socket.on(
      "board-pointer-updated",
      (data: {
        userId: string;
        userName: string;
        userAvatar?: string;
        pointer: { x: number; y: number };
        color: string;
      }) => {
        this.updateCollaborator(data.userId as unknown as SocketId, {
          pointer: data.pointer,
          username: data.userName,
          color: data.color,
          avatarUrl: data.userAvatar,
        });
      },
    );

    // Board idle updates
    this.portal.socket.on(
      "board-idle-updated",
      (data: { userId: string; idle: boolean }) => {
        this.updateCollaborator(data.userId as unknown as SocketId, {
          userState: data.idle ? UserIdleState.IDLE : UserIdleState.ACTIVE,
        });
      },
    );

    // Full sync
    this.portal.socket.on(
      "board-synced",
      (data: { userId: string; elements: any[]; timestamp: string }) => {
        if (data.elements) {
          this.handleRemoteSceneUpdate(
            this._reconcileElements(data.elements),
          );
        }
      },
    );

    // First-in-room (we're the only user — resolve with fetched scene)
    this.portal.socket.on("_first-in-room", async () => {
      this.portal.socket?.off("_first-in-room");
      if (preloadedScene) {
        // The scene is already loaded from the API by SpryBoard's initializeScene.
        // Just mark the room as initialized and let the caller use the pre-loaded scene.
        const elements = this.excalidrawAPI.getSceneElementsIncludingDeleted();
        const syncable = getSyncableElements(
          elements as readonly OrderedExcalidrawElement[],
        );
        this.setLastBroadcastedOrReceivedSceneVersion(getSceneVersion(syncable));
        clearTimeout(this.socketInitializationTimer!);
        if (this.portal.socket && this.fallbackInitializationHandler) {
          this.portal.socket.off(
            "connect_error",
            this.fallbackInitializationHandler,
          );
        }
        this.portal.socketInitialized = true;
        scenePromise.resolve(null);
        return;
      }
      const sceneData = await this.initializeRoom({
        fetchScene: true,
        roomLinkData: existingRoomLinkData,
      });
      scenePromise.resolve(sceneData);
    });

    this.portal.socket.on(
      WS_EVENTS.USER_FOLLOW_ROOM_CHANGE,
      (followedBy: SocketId[]) => {
        this.excalidrawAPI.updateScene({
          appState: { followedBy: new Set(followedBy) },
        });
        this.relayVisibleSceneBounds({ force: true });
      },
    );

    this.initializeIdleDetector();
    this.setActiveRoomLink(window.location.href);

    return scenePromise;
  };

  private initializeRoom = async ({
    fetchScene,
    roomLinkData,
    preloadedScene,
  }:
    | {
      fetchScene: true;
      roomLinkData: { roomId: string; roomKey: string } | null;
      preloadedScene?: boolean;
    }
    | { fetchScene: false; roomLinkData?: null; preloadedScene?: boolean }) => {
    clearTimeout(this.socketInitializationTimer!);
    if (this.portal.socket && this.fallbackInitializationHandler) {
      this.portal.socket.off(
        "connect_error",
        this.fallbackInitializationHandler,
      );
    }
    // If the SpryBoard app already loaded the scene from the API, don't reset
    // and re-fetch — that would clobber the loaded data and cause a flash-then-blank.
    if (preloadedScene) {
      const elements = this.excalidrawAPI.getSceneElementsIncludingDeleted();
      const syncable = getSyncableElements(
        elements as readonly OrderedExcalidrawElement[],
      );
      this.setLastBroadcastedOrReceivedSceneVersion(getSceneVersion(syncable));
      this.portal.socketInitialized = true;
      return null;
    }
    if (fetchScene && roomLinkData && this.portal.socket) {
      this.excalidrawAPI.resetScene();

      try {
        const elements = await loadFromBackend(
          roomLinkData.roomId,
          roomLinkData.roomKey,
          this.portal.socket,
        );
        if (elements) {
          this.setLastBroadcastedOrReceivedSceneVersion(
            getSceneVersion(elements),
          );
          this.replaceBoardSnapshot(elements, "spryboard:remote:initial-room");

          return {
            elements,
            scrollToContent: true,
          };
        }
      } catch (error: any) {
        console.error(error);
      } finally {
        this.portal.socketInitialized = true;
      }
    } else {
      this.portal.socketInitialized = true;
    }
    return null;
  };

  private _reconcileElements = (
    remoteElements: readonly ExcalidrawElement[],
  ): ReconciledExcalidrawElement[] => {
    const localElements = this.getSceneElementsIncludingDeleted();
    const appState = this.excalidrawAPI.getAppState();
    const restoredRemoteElements = restoreElements(
      remoteElements,
      this.excalidrawAPI.getSceneElementsMapIncludingDeleted(),
    );
    const reconciledElements = reconcileElements(
      localElements,
      restoredRemoteElements as RemoteExcalidrawElement[],
      appState,
    );

    this.setLastBroadcastedOrReceivedSceneVersion(
      getSceneVersion(reconciledElements),
    );

    return reconciledElements;
  };

  private loadImageFiles = throttle(async () => {
    const { loadedFiles, erroredFiles } =
      await this.fetchImageFilesFromBackend({
        elements: this.excalidrawAPI.getSceneElementsIncludingDeleted(),
      });

    this.excalidrawAPI.addFiles(loadedFiles);

    updateStaleImageStatuses({
      excalidrawAPI: this.excalidrawAPI,
      erroredFiles,
      elements: this.excalidrawAPI.getSceneElementsIncludingDeleted(),
    });
  }, LOAD_IMAGES_TIMEOUT);

  private handleRemoteSceneUpdate = (
    elements: ReconciledExcalidrawElement[],
  ) => {
    this.excalidrawAPI.updateScene({
      elements,
      captureUpdate: CaptureUpdateAction.NEVER,
    });
    this.replaceBoardSnapshot(elements, "spryboard:remote:scene");
    this.crdt?.appendOperation(
      "board-scene-reconciled",
      {
        boardId: this.portal.roomId,
        elementCount: elements.length,
      },
      "spryboard:remote:scene:op",
    );

    this.loadImageFiles();
  };

  private onPointerMove = () => {
    if (this.idleTimeoutId) {
      window.clearTimeout(this.idleTimeoutId);
      this.idleTimeoutId = null;
    }

    this.idleTimeoutId = window.setTimeout(this.reportIdle, IDLE_THRESHOLD);

    if (!this.activeIntervalId) {
      this.activeIntervalId = window.setInterval(
        this.reportActive,
        ACTIVE_THRESHOLD,
      );
    }
  };

  private onVisibilityChange = () => {
    if (document.hidden) {
      if (this.idleTimeoutId) {
        window.clearTimeout(this.idleTimeoutId);
        this.idleTimeoutId = null;
      }
      if (this.activeIntervalId) {
        window.clearInterval(this.activeIntervalId);
        this.activeIntervalId = null;
      }
      this.onIdleStateChange(UserIdleState.AWAY);
    } else {
      this.idleTimeoutId = window.setTimeout(this.reportIdle, IDLE_THRESHOLD);
      this.activeIntervalId = window.setInterval(
        this.reportActive,
        ACTIVE_THRESHOLD,
      );
      this.onIdleStateChange(UserIdleState.ACTIVE);
    }
  };

  private reportIdle = () => {
    this.onIdleStateChange(UserIdleState.IDLE);
    if (this.activeIntervalId) {
      window.clearInterval(this.activeIntervalId);
      this.activeIntervalId = null;
    }
  };

  private reportActive = () => {
    this.onIdleStateChange(UserIdleState.ACTIVE);
  };

  private initializeIdleDetector = () => {
    document.addEventListener(EVENT.POINTER_MOVE, this.onPointerMove);
    document.addEventListener(EVENT.VISIBILITY_CHANGE, this.onVisibilityChange);
  };

  private isCurrentUser = (socketId: SocketId) => {
    const currentUserId = this.props.currentUserId;
    return !!currentUserId && socketId === (currentUserId as unknown as SocketId);
  };

  setCollaborators(sockets: SocketId[]) {
    const collaborators: InstanceType<typeof Collab>["collaborators"] =
      new Map();
    for (const socketId of sockets) {
      const isCurrentUser = this.isCurrentUser(socketId);
      collaborators.set(
        socketId,
        Object.assign({}, this.collaborators.get(socketId), {
          isCurrentUser,
          avatarUrl:
            isCurrentUser && this.props.currentUserAvatar
              ? this.props.currentUserAvatar
              : this.collaborators.get(socketId)?.avatarUrl,
        }),
      );
    }
    this.collaborators = collaborators;
    this.excalidrawAPI.updateScene({ collaborators });
    this.syncCollaboratorsToCrdt("spryboard:presence:set-collaborators");
  }

  updateCollaborator = (socketId: SocketId, updates: Partial<Collaborator>) => {
    const collaborators = new Map(this.collaborators);
    const isCurrentUser = this.isCurrentUser(socketId);
    const avatarUrl =
      updates.avatarUrl ||
      (isCurrentUser ? this.props.currentUserAvatar : undefined);
    const user: Mutable<Collaborator> = Object.assign(
      {},
      collaborators.get(socketId),
      updates,
      {
        isCurrentUser,
        avatarUrl,
      },
    );
    collaborators.set(socketId, user);
    this.collaborators = collaborators;

    this.excalidrawAPI.updateScene({
      collaborators,
    });
    this.syncCollaboratorsToCrdt("spryboard:presence:update-collaborator");
  };

  removeCollaborator = (socketId: SocketId) => {
    if (!this.collaborators.has(socketId)) {
      return;
    }
    const collaborators = new Map(this.collaborators);
    collaborators.delete(socketId);
    this.collaborators = collaborators;
    this.excalidrawAPI.updateScene({ collaborators });
    this.syncCollaboratorsToCrdt("spryboard:presence:remove-collaborator");
  };

  public setLastBroadcastedOrReceivedSceneVersion = (version: number) => {
    this.lastBroadcastedOrReceivedSceneVersion = version;
  };

  public getLastBroadcastedOrReceivedSceneVersion = () => {
    return this.lastBroadcastedOrReceivedSceneVersion;
  };

  public getSceneElementsIncludingDeleted = () => {
    return this.excalidrawAPI.getSceneElementsIncludingDeleted();
  };

  onPointerUpdate = throttle(
    (payload: {
      pointer: SocketUpdateDataSource["MOUSE_LOCATION"]["payload"]["pointer"];
      button: SocketUpdateDataSource["MOUSE_LOCATION"]["payload"]["button"];
      pointersMap: Gesture["pointers"];
    }) => {
      if (payload.pointersMap.size < 2 && this.portal.socket) {
        this.portal.broadcastMouseLocation(payload);
        this.updateCollaborator(this.portal.socket.id as SocketId, {
          username: this.state.username,
          pointer: payload.pointer,
        });
        this.crdt?.appendOperation(
          "board-pointer-local",
          {
            boardId: this.portal.roomId,
            pointer: payload.pointer,
          },
          "spryboard:local:pointer",
        );
      }
    },
    CURSOR_SYNC_TIMEOUT,
  );

  relayVisibleSceneBounds = (props?: { force: boolean }) => {
    const appState = this.excalidrawAPI.getAppState();

    if (this.portal.socket && (appState.followedBy.size > 0 || props?.force)) {
      this.portal.broadcastVisibleSceneBounds(
        {
          sceneBounds: getVisibleSceneBounds(appState),
        },
        `follow@${this.portal.socket.id}`,
      );
    }
  };

  onIdleStateChange = (userState: UserIdleState) => {
    this.portal.broadcastIdleChange(userState);
    if (this.portal.socket?.id) {
      this.updateCollaborator(this.portal.socket.id as SocketId, {
        username: this.state.username,
        userState,
      });
      this.crdt?.appendOperation(
        "board-idle-local",
        {
          boardId: this.portal.roomId,
          userState,
        },
        "spryboard:local:idle",
      );
    }
  };

  broadcastElements = (elements: readonly OrderedExcalidrawElement[]) => {
    if (
      getSceneVersion(elements) >
      this.getLastBroadcastedOrReceivedSceneVersion()
    ) {
      this.replaceBoardSnapshot(elements, "spryboard:local:elements");
      this.crdt?.appendOperation(
        "board-update",
        {
          boardId: this.portal.roomId,
          elementCount: elements.length,
          sceneVersion: getSceneVersion(elements),
        },
        "spryboard:local:elements:op",
      );
      this.portal.broadcastScene(WS_SUBTYPES.UPDATE, elements, false);
      this.lastBroadcastedOrReceivedSceneVersion = getSceneVersion(elements);
      this.queueBroadcastAllElements();
    }
  };

  syncElements = (elements: readonly OrderedExcalidrawElement[]) => {
    this.broadcastElements(elements);
    this.queueSaveToBackend();
  };

  queueBroadcastAllElements = throttle(() => {
    const elements = this.excalidrawAPI.getSceneElementsIncludingDeleted();
    this.replaceBoardSnapshot(elements, "spryboard:local:full-scene");
    this.crdt?.appendOperation(
      "board-full-sync",
      {
        boardId: this.portal.roomId,
        elementCount: elements.length,
      },
      "spryboard:local:full-scene:op",
    );
    this.portal.broadcastScene(
      WS_SUBTYPES.UPDATE,
      elements,
      true,
    );
    const currentVersion = this.getLastBroadcastedOrReceivedSceneVersion();
    const newVersion = Math.max(
      currentVersion,
      getSceneVersion(this.getSceneElementsIncludingDeleted()),
    );
    this.setLastBroadcastedOrReceivedSceneVersion(newVersion);
  }, SYNC_FULL_SCENE_INTERVAL_MS);

  queueSaveToBackend = throttle(
    () => {
      if (this.portal.socketInitialized) {
        this.saveCollabRoomToBackend(
          getSyncableElements(
            this.excalidrawAPI.getSceneElementsIncludingDeleted(),
          ),
        );
      }
    },
    SYNC_FULL_SCENE_INTERVAL_MS,
    { leading: false },
  );

  // Alias for backward compat (referenced as queueSaveToFirebase in some places)
  queueSaveToFirebase = this.queueSaveToBackend;

  setUsername = (username: string) => {
    this.setState({ username });
    saveUsernameToLocalStorage(username);
  };

  getUsername = () => this.state.username;

  setActiveRoomLink = (activeRoomLink: string | null) => {
    this.setState({ activeRoomLink });
    appJotaiStore.set(activeRoomLinkAtom, activeRoomLink);
  };

  getActiveRoomLink = () => this.state.activeRoomLink;

  setErrorIndicator = (errorMessage: string | null) => {
    appJotaiStore.set(collabErrorIndicatorAtom, {
      message: errorMessage,
      nonce: Date.now(),
    });
  };

  resetErrorIndicator = (resetDialogNotifiedErrors = false) => {
    appJotaiStore.set(collabErrorIndicatorAtom, { message: null, nonce: 0 });
    if (resetDialogNotifiedErrors) {
      this.setState({
        dialogNotifiedErrors: {},
      });
    }
  };

  setErrorDialog = (errorMessage: string | null) => {
    this.setState({
      errorMessage,
    });
  };

  render() {
    const { errorMessage } = this.state;

    return (
      <>
        {errorMessage != null && (
          <ErrorDialog onClose={() => this.setErrorDialog(null)}>
            {errorMessage}
          </ErrorDialog>
        )}
      </>
    );
  }
}

declare global {
  interface Window {
    collab: InstanceType<typeof Collab>;
  }
}

if (isTestEnv() || isDevEnv()) {
  window.collab = window.collab || ({} as Window["collab"]);
}

export default Collab;

export type TCollabClass = Collab;
